import { Component, Input, QueryList, ChangeDetectionStrategy, ChangeDetectorRef, ElementRef, AfterViewInit, ContentChildren, Output, EventEmitter, OnDestroy } from '@angular/core';
import { SplitterPanelComponent } from './splitter-panel.component';

export type NcLayoutType = 'horizontal' | 'vertical';

export interface NcSplitterResizeEvent {
  gutterIndex: number;
  sizes: number[];
  delta?: number;
  mouseEvent: MouseEvent | TouchEvent;
  containerSize: [number, number];
}

@Component({
  selector: 'nc-splitter',
  templateUrl: './splitter.component.html',
  styleUrls: ['./splitter.component.less'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class SplitterComponent implements AfterViewInit, OnDestroy {
  /** 布局方向 */
  @Input() ncLayout: NcLayoutType = 'horizontal';
  /** 分割线大小 */
  @Input() ncGutterSize: number = 2;
  /** 分割线热区大小（可点击区域），默认为分割线大小的3倍 */
  @Input() ncGutterHitSize: number = this.ncGutterSize * 3;
  /** 分割线颜色 */
  @Input() ncGutterColor: string = 'rgba(0, 0, 0, 0.04)';
  /** 分割线悬停颜色 */
  @Input() ncGutterHoverColor: string = 'rgb(230, 244, 255)';
  /** 分割线移动颜色 */
  @Input() ncGutterMoveColor: string = 'rgb(186, 224, 255)';
  /** 分割线图标颜色 */
  @Input() ncGutterIconColor: string = 'rgba(0, 0, 0, 0.15)';
  /** 延迟渲染模式 */
  private _lazy: boolean = false;
  @Input()
  set ncLazy(val: boolean | string) {
    this._lazy = val !== null && val !== undefined && val !== false && val !== 'false';
  }
  get ncLazy(): boolean {
    return this._lazy;
  }
  /** 面板组件 */
  @ContentChildren(SplitterPanelComponent) panelComponents!: QueryList<SplitterPanelComponent>;
  /** 分割线移动事件 */
  @Output() ncResize = new EventEmitter<NcSplitterResizeEvent>();
  /** 分割线开始移动事件 */
  @Output() ncResizeStart = new EventEmitter<NcSplitterResizeEvent>();
  /** 分割线结束移动事件 */
  @Output() ncResizeEnd = new EventEmitter<NcSplitterResizeEvent>();

  panels: SplitterPanelComponent[] = [];
  isResizing = false;
  resizingIndex = -1;
  startPosition = 0;
  startSizes: number[] = [];
  curGutterDom: HTMLElement | null = null;

  // 懒加载模式相关属性
  lazySizes: number[] = [];  // 懒加载模式下计算的临时尺寸
  lazyOffset = 0;  // 分割线的偏移量
  previewLine: HTMLElement | null = null;  // 预览线元素

  constructor(private cdr: ChangeDetectorRef, private el: ElementRef) { }

  ngAfterViewInit() {
    this.panels = this.panelComponents.toArray();
    this.normalizeSizes();
    this.cdr.detectChanges();
  }

  normalizeSizes() {
    // 计算已设置尺寸的面板总和
    const configuredPanels = this.panels.filter(panel => panel.ncPanelSize);
    const configuredSize = configuredPanels.reduce((sum, panel) => sum + panel.ncPanelSize!, 0);

    // 计算未设置尺寸的面板数量
    const unconfiguredCount = this.panels.length - configuredPanels.length;

    // 为未设置尺寸的面板平均分配剩余空间
    const remainingSize = 100 - configuredSize;
    const defaultSize = unconfiguredCount > 0 ? remainingSize / unconfiguredCount : 0;

    // 设置每个面板的最终尺寸
    this.panels.forEach(panel => {
      if (!panel.ncPanelSize) {
        panel.ncPanelSize = defaultSize;
      }
    });

    // 若面板的最小尺寸大于面板的尺寸，则将面板的尺寸设置为最小尺寸，即无法再缩小
    this.panels.forEach(panel => {
      if (panel.ncMinSize && panel.ncMinSize > panel.ncPanelSize!) {
        panel.ncMinSize = panel.ncPanelSize!;
      }
    });
  }

  onMouseDown(event: MouseEvent, index: number) {
    this.startResize(event, index, 'mouse');
  }

  onTouchStart(event: TouchEvent, index: number) {
    // 防止默认的滚动行为
    event.preventDefault();
    this.startResize(event, index, 'touch');
  }

  private startResize(event: MouseEvent | TouchEvent, index: number, type: 'mouse' | 'touch') {
    event.preventDefault(); // 防止默认行为
    this.curGutterDom = event.target as HTMLElement;
    this.curGutterDom.style.backgroundColor = this.ncGutterMoveColor;
    this.isResizing = true;
    this.resizingIndex = index;

    // 获取初始位置（兼容鼠标和触摸事件）
    const clientX = type === 'mouse' ? (event as MouseEvent).clientX : (event as TouchEvent).touches[0].clientX;
    const clientY = type === 'mouse' ? (event as MouseEvent).clientY : (event as TouchEvent).touches[0].clientY;
    this.startPosition = this.ncLayout === 'horizontal' ? clientX : clientY;
    this.startSizes = this.panels.map(panel => panel.ncPanelSize || 0);

    // 禁用文本选择
    document.body.style.userSelect = 'none';
    document.body.style.webkitUserSelect = 'none';

    // 设置鼠标指针样式（仅对鼠标事件有效）
    if (type === 'mouse') {
      document.body.style.cursor = this.ncLayout === 'horizontal' ? 'col-resize' : 'row-resize';
    }

    // 对于触摸事件，防止页面滚动和缩放
    if (type === 'touch') {
      // 禁用触摸滚动
      document.body.style.overflow = 'hidden';
      document.body.style.touchAction = 'none';
    }

    // 添加对应的事件监听器
    if (type === 'mouse') {
      document.addEventListener('mousemove', this.onMouseMove);
      document.addEventListener('mouseup', this.onMouseUp);
    } else {
      document.addEventListener('touchmove', this.onTouchMove, { passive: false });
      document.addEventListener('touchend', this.onTouchEnd);
      document.addEventListener('touchcancel', this.onTouchEnd);
    }

    this.ncResizeStart.emit({
      gutterIndex: index,
      sizes: this.panels.map(panel => panel.ncPanelSize || 0),
      mouseEvent: event,
      containerSize: [this.el.nativeElement.offsetWidth, this.el.nativeElement.offsetHeight]
    });
  }

  onMouseMove = (event: MouseEvent) => {
    this.handleMove(event, 'mouse');
  }

  onTouchMove = (event: TouchEvent) => {
    // 防止页面滚动
    event.preventDefault();
    this.handleMove(event, 'touch');
  }

  private handleMove(event: MouseEvent | TouchEvent, type: 'mouse' | 'touch') {
    if (!this.isResizing) return;

    // 获取当前位置（兼容鼠标和触摸事件）
    const clientX = type === 'mouse' ? (event as MouseEvent).clientX : (event as TouchEvent).touches[0].clientX;
    const clientY = type === 'mouse' ? (event as MouseEvent).clientY : (event as TouchEvent).touches[0].clientY;
    const currentPosition = this.ncLayout === 'horizontal' ? clientX : clientY;
    let delta = ((currentPosition - this.startPosition) / this.getContainerSize()) * 100;

    const newSizes = [...this.startSizes];
    const leftPanelMinSize = this.panels[this.resizingIndex].ncMinSize || 0;
    const rightPanelMinSize = this.panels[this.resizingIndex + 1].ncMinSize || 0;

    // 计算实际可移动的范围，限制delta
    //左面板最多能扩展多少（受右面板最小尺寸限制）
    const maxLeftExpansion = this.startSizes[this.resizingIndex + 1] - rightPanelMinSize;
    //右面板最多能扩展多少（受左面板最小尺寸限制）
    const maxRightExpansion = this.startSizes[this.resizingIndex] - leftPanelMinSize;

    // 限制delta在有效范围内
    if (delta > maxLeftExpansion) {
      //如果delta > maxLeftExpansion，说明左面板想要扩展得太多，会导致右面板小于最小尺寸，所以限制delta = maxLeftExpansion
      delta = maxLeftExpansion;
    } else if (delta < -maxRightExpansion) {
      //如果delta < -maxRightExpansion，说明右面板想要扩展得太多，会导致左面板小于最小尺寸，所以限制delta = -maxRightExpansion
      delta = -maxRightExpansion;
    }

    newSizes[this.resizingIndex] = this.startSizes[this.resizingIndex] + delta;
    newSizes[this.resizingIndex + 1] = this.startSizes[this.resizingIndex + 1] - delta;

    if (this.ncLazy) {
      // 懒加载模式：显示预览线，不改变面板尺寸
      this.lazySizes = [...newSizes];
      this.lazyOffset = (delta / 100) * this.getContainerSize();

      // 创建或更新预览线
      if (!this.previewLine) {
        this.previewLine = this.createPreviewLine();
        this.el.nativeElement.appendChild(this.previewLine);
      }

      // 计算预览线的位置
      const gutterRect = this.curGutterDom!.getBoundingClientRect();
      const containerRect = this.el.nativeElement.getBoundingClientRect();

      if (this.ncLayout === 'horizontal') {
        const originalLeft = gutterRect.left - containerRect.left;
        this.previewLine.style.left = `${originalLeft + this.lazyOffset}px`;
      } else {
        const originalTop = gutterRect.top - containerRect.top;
        this.previewLine.style.top = `${originalTop + this.lazyOffset}px`;
      }
    } else {
      // 正常模式：实时更新面板尺寸
      const totalSize = newSizes.reduce((sum, size) => sum + size, 0);
      this.panels.forEach((panel, i) => panel.ncPanelSize = (newSizes[i] / totalSize) * 100);
      this.cdr.detectChanges();
    }

    this.ncResize.emit({
      gutterIndex: this.resizingIndex,
      sizes: this.ncLazy ? this.lazySizes : this.panels.map(panel => panel.ncPanelSize || 0),
      delta: delta,
      mouseEvent: event,
      containerSize: [this.el.nativeElement.offsetWidth, this.el.nativeElement.offsetHeight]
    });
  }

  onMouseUp = (event: MouseEvent) => {
    this.endResize(event, 'mouse');
  }

  onTouchEnd = (event: TouchEvent) => {
    this.endResize(event, 'touch');
  }

  private endResize(event: MouseEvent | TouchEvent, type: 'mouse' | 'touch') {
    if (this.curGutterDom) {
      this.curGutterDom.style.backgroundColor = '';  // 清空内联样式，让CSS样式生效

      if (this.ncLazy) {
        // 懒加载模式：移除预览线，应用最终面板尺寸
        this.removePreviewLine();

        if (this.lazySizes.length > 0) {
          const totalSize = this.lazySizes.reduce((sum, size) => sum + size, 0);
          this.panels.forEach((panel, i) => panel.ncPanelSize = (this.lazySizes[i] / totalSize) * 100);
          this.cdr.detectChanges();
        }

        // 清空懒加载状态
        this.lazySizes = [];
        this.lazyOffset = 0;
      }
    }

    // 恢复文本选择和鼠标指针
    document.body.style.userSelect = '';
    document.body.style.webkitUserSelect = '';
    document.body.style.cursor = '';

    // 对于触摸事件，恢复页面滚动和缩放
    if (type === 'touch') {
      document.body.style.overflow = '';
      document.body.style.touchAction = '';
    }

    this.isResizing = false;

    // 移除对应的事件监听器
    if (type === 'mouse') {
      document.removeEventListener('mousemove', this.onMouseMove);
      document.removeEventListener('mouseup', this.onMouseUp);
    } else {
      document.removeEventListener('touchmove', this.onTouchMove);
      document.removeEventListener('touchend', this.onTouchEnd);
      document.removeEventListener('touchcancel', this.onTouchEnd);
    }

    this.ncResizeEnd.emit({
      gutterIndex: this.resizingIndex,
      sizes: this.panels.map(panel => panel.ncPanelSize || 0),
      mouseEvent: event,
      containerSize: [this.el.nativeElement.offsetWidth, this.el.nativeElement.offsetHeight]
    });
  }

  getContainerSize(): number {
    return this.ncLayout === 'horizontal' ? this.el.nativeElement.offsetWidth : this.el.nativeElement.offsetHeight;
  }

  // 创建预览线
  createPreviewLine(): HTMLElement {
    const previewLine = document.createElement('div');
    previewLine.className = `nc-splitter-preview-line nc-splitter-preview-line-${this.ncLayout}`;
    previewLine.style.position = 'absolute';
    previewLine.style.backgroundColor = this.ncGutterHoverColor;
    previewLine.style.pointerEvents = 'none';
    previewLine.style.zIndex = '1000';

    if (this.ncLayout === 'horizontal') {
      previewLine.style.width = `${this.ncGutterSize}px`;
      previewLine.style.height = '100%';
      previewLine.style.top = '0';
    } else {
      previewLine.style.height = `${this.ncGutterSize}px`;
      previewLine.style.width = '100%';
      previewLine.style.left = '0';
    }

    return previewLine;
  }

  // 移除预览线
  removePreviewLine() {
    if (this.previewLine && this.previewLine.parentNode) {
      this.previewLine.parentNode.removeChild(this.previewLine);
      this.previewLine = null;
    }
  }

  ngOnDestroy() {
    // 清理预览线
    this.removePreviewLine();

    // 清理所有事件监听器
    document.removeEventListener('mousemove', this.onMouseMove);
    document.removeEventListener('mouseup', this.onMouseUp);
    document.removeEventListener('touchmove', this.onTouchMove);
    document.removeEventListener('touchend', this.onTouchEnd);
    document.removeEventListener('touchcancel', this.onTouchEnd);

    // 恢复所有样式
    document.body.style.userSelect = '';
    document.body.style.webkitUserSelect = '';
    document.body.style.cursor = '';
    document.body.style.overflow = '';
    document.body.style.touchAction = '';
  }
}